import React from 'react'
import {
  Phone,
  Mail,
  MessageSquare,
  CheckCircle2,
  XCircle,
  ListChecks,
} from 'lucide-react'
import type { ChatMessage } from '../../types'
import type {
  HandoffActionExecutionEventData,
  RecipientAttribution,
} from '@super/shared-types'
import {
  formatUSPhoneForDisplay as formatPhone,
  ActionType,
  attributionKey,
  buildAttributionMap,
  formatAttributedRecipient,
  type PhoneCallResults,
} from '@super/shared-types'
import { InlineAudioPlayer } from './InlineAudioPlayer'
import { formatMessageTime } from '@/utils/date-formatters'
import { TimeDisplay } from '@/components/common/TimeDisplay'
import { MessageActionsMenu } from '../MessageActionsMenu'

function getPhoneTransferResultLabel(phoneResults: PhoneCallResults): string {
  if (phoneResults.cancelled) {
    return 'Cancelled'
  }
  if (!phoneResults.transferType) {
    return 'Transfer attempted'
  }
  if (phoneResults.transferType === 'SIMPLE') {
    return 'Transferred'
  }
  if (phoneResults.successful) {
    return 'Accepted'
  }
  if (phoneResults.declineReason === 'VOICEMAIL') {
    return phoneResults.voicemailLeft ? 'Voicemail (message left)' : 'Voicemail'
  }
  if (phoneResults.answered) {
    if (phoneResults.declineReason === 'TIMEOUT') {
      return 'Declined (timeout)'
    }
    if (phoneResults.declineReason === 'AMBIGUOUS') {
      return 'Declined (no clear response)'
    }
    return 'Declined'
  }
  if (phoneResults.declineReason === 'NOT_CONNECTED') {
    return 'Not connected'
  }
  return 'Not answered'
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

interface ActionExecutionMessageProps {
  message: ChatMessage
  threadId?: string
  messageId?: string
  companyId?: string
  sessionId?: string
  onSessionDetailsClick?: (sessionId: string) => void
}

export const ActionExecutionMessage: React.FC<ActionExecutionMessageProps> = ({
  message,
  threadId,
  messageId,
  companyId,
  sessionId,
  onSessionDetailsClick,
}) => {
  const handoffData = message.data as HandoffActionExecutionEventData
  const actionElements: React.ReactNode[] = []

  // channel+address → attribution for every recipient (positive + negative).
  // Prefer the new channel-agnostic roster; fall back to legacy phone-only
  // roleAttribution for events persisted before that field existed.
  const recipientAttributions: RecipientAttribution[] =
    handoffData.recipientAttributions ??
    (handoffData.roleAttribution ?? []).map(a => ({
      address: a.number,
      channel: 'PHONE_CALL' as const,
      staffName: a.staffName,
      roleName: a.roleName,
    }))
  const attributionByKey = buildAttributionMap(recipientAttributions)

  const phoneActions =
    handoffData.actionResults?.filter(
      result => result.actionType === ActionType.PHONE_CALL,
    ) || []

  phoneActions.forEach((action, index) => {
    const phoneResults = action.results as PhoneCallResults | undefined
    const phoneNumber = action.parameters?.number as string | undefined
    const extension = action.parameters?.extension as string | undefined
    const staffName = action.parameters?.staffName as string | undefined
    const recordingUrl = phoneResults?.recordingUrl
    const isCancelled = phoneResults?.cancelled === true

    const formattedNumber = phoneNumber
      ? formatPhone(phoneNumber)
      : 'Unknown number'
    const attribution = phoneNumber
      ? attributionByKey.get(attributionKey('PHONE_CALL', phoneNumber))
      : undefined
    // Prefer the recipient attribution; else the staffName stamp; else number.
    const transferNumber = attribution
      ? formatAttributedRecipient(attribution)
      : staffName
        ? `${staffName} ${formattedNumber}`
        : formattedNumber
    const resultLabel = phoneResults
      ? capitalize(getPhoneTransferResultLabel(phoneResults))
      : 'Transfer attempted'

    actionElements.push(
      recordingUrl && !isCancelled ? (
        <InlineAudioPlayer
          key={`phone-${index}`}
          recordingUrl={recordingUrl}
          transferNumber={transferNumber}
          extension={extension}
          resultLabel={resultLabel}
          successful={action.successful}
        />
      ) : (
        <div
          key={`phone-${index}`}
          className={`flex items-center gap-2 ${isCancelled ? 'text-muted-foreground opacity-50' : 'text-muted-foreground'}`}
        >
          <Phone className="w-3 h-3 flex-shrink-0" />
          <span className="text-xs truncate">
            {transferNumber}
            {extension && ` x ${extension}`}
          </span>
          <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-muted-foreground">{resultLabel}</span>
            {isCancelled ? (
              <XCircle className="w-3.5 h-3.5 text-muted-foreground/50" />
            ) : action.successful ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-success-dim" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </div>
        </div>
      ),
    )
  })

  const emailActions =
    handoffData.actionResults?.filter(
      result => result.actionType === ActionType.EMAIL,
    ) || []

  emailActions.forEach((action, index) => {
    const recipients = action.parameters?.to
    // Attribute each address when known; else show the raw email.
    const recipientText = Array.isArray(recipients)
      ? recipients
          .map(addr => {
            const attribution = attributionByKey.get(
              attributionKey('EMAIL', addr as string),
            )
            return attribution
              ? formatAttributedRecipient(attribution)
              : (addr as string)
          })
          .join(', ')
      : 'unknown recipients'
    const resultLabel = action.successful ? 'Sent' : 'Failed'

    actionElements.push(
      <div
        key={`email-${index}`}
        className="flex items-center gap-2 text-muted-foreground"
      >
        <Mail className="w-3 h-3 flex-shrink-0" />
        <span className="text-xs truncate">{recipientText}</span>
        <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          <span className="text-xs text-muted-foreground">{resultLabel}</span>
          {action.successful ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-success-dim" />
          ) : (
            <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </div>
      </div>,
    )
  })

  const smsActions =
    handoffData.actionResults?.filter(
      result => result.actionType === ActionType.SMS,
    ) || []

  smsActions.forEach((action, index) => {
    const recipients = action.parameters?.to
    // Attribute each number when known; else the formatted number.
    const formattedRecipients = Array.isArray(recipients)
      ? recipients
          .map(phone => {
            const attribution = attributionByKey.get(
              attributionKey('SMS', phone as string),
            )
            return attribution
              ? formatAttributedRecipient(attribution)
              : formatPhone(phone as string)
          })
          .join(', ')
      : 'unknown recipients'
    const resultLabel = action.successful ? 'Sent' : 'Failed'

    actionElements.push(
      <div
        key={`sms-${index}`}
        className="flex items-center gap-2 text-muted-foreground"
      >
        <MessageSquare className="w-3 h-3 flex-shrink-0" />
        <span className="text-xs truncate">{formattedRecipients}</span>
        <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          <span className="text-xs text-muted-foreground">{resultLabel}</span>
          {action.successful ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-success-dim" />
          ) : (
            <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </div>
      </div>,
    )
  })

  // Per-channel schedule-misses: a channel whose intended recipients (@role/
  // @staff) resolved to nobody (blank rotation day, everyone off). Rendered
  // beside the channels that did reach someone. The channel icon already
  // conveys which channel this is, so no textual channel label is needed.
  const channelIcon = {
    PHONE_CALL: Phone,
    SMS: MessageSquare,
    EMAIL: Mail,
  } as const
  const unreachableElements: React.ReactNode[] = (
    handoffData.unreachableChannels ?? []
  ).map((entry, index) => {
    const Icon = channelIcon[entry.channel]
    const names = entry.recipients.map(r => `@${r}`).join(', ')
    return (
      <div
        key={`unreachable-${index}`}
        className="flex items-center gap-2 text-muted-foreground"
      >
        <Icon className="w-3 h-3 flex-shrink-0" />
        <span className="text-xs truncate">
          No one was available
          {names ? ` from ${names}` : ''}
        </span>
        <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
      </div>
    )
  })

  // A specific staff was resolved but has no address on this channel (often a
  // sub covering an off member) — distinct from "no one available". Renders
  // "No {email|phone number} for @Sub (covering for @Off)".
  const channelNoun = {
    PHONE_CALL: 'phone number',
    SMS: 'phone number',
    EMAIL: 'email',
  } as const
  const missingContactElements: React.ReactNode[] = recipientAttributions
    .filter(a => a.missingContact)
    .map((attr, index) => {
      const Icon = channelIcon[attr.channel]
      return (
        <div
          key={`missing-${index}`}
          className="flex items-center gap-2 text-muted-foreground"
        >
          <Icon className="w-3 h-3 flex-shrink-0" />
          <span className="text-xs truncate">
            No {channelNoun[attr.channel]} for {formatAttributedRecipient(attr)}
          </span>
          <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
            <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
          </div>
        </div>
      )
    })

  return (
    <div className="my-3 group relative">
      <div className="flex justify-center mb-2 relative group/card">
        <div className="text-muted-foreground text-xs flex items-center gap-2 relative">
          <ListChecks className="w-3 h-3" />
          <span>Actions</span>
          <span className="text-muted-foreground/60">
            •{' '}
            <TimeDisplay
              date={message.createdAt}
              formatter={formatMessageTime}
              className="inline"
            />
          </span>
        </div>
      </div>

      <div className="flex justify-center">
        <div className="bg-muted border border-border rounded-lg px-4 py-3 max-w-md w-full relative">
          {threadId && messageId && (
            <div className="absolute -right-10 top-2 z-30">
              <MessageActionsMenu
                threadId={threadId}
                messageId={messageId}
                companyId={companyId}
                sessionId={sessionId}
                onSessionDetailsClick={onSessionDetailsClick}
                tooltipSide="right"
              />
            </div>
          )}
          <div className="space-y-2 text-xs">
            {actionElements.length > 0 ||
            unreachableElements.length > 0 ||
            missingContactElements.length > 0 ? (
              [
                ...actionElements,
                ...missingContactElements,
                ...unreachableElements,
              ]
            ) : (
              <span className="text-muted-foreground">(None executed)</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
